/**
 * TanStack Query のキャッシュキー定義
 *
 * ファクトリーパターンでキーを一元管理し、
 * invalidateQueries 時の一括無効化を容易にする。
 *
 * @example
 * // 処方箋リストの取得
 * useQuery({ queryKey: queryKeys.prescriptions.list() })
 *
 * // 処方箋関連のキャッシュを一括無効化
 * queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.all })
 */
export const queryKeys = {
  // 処方箋
  prescriptions: {
    all: ['prescriptions'] as const,
    list: () => [...queryKeys.prescriptions.all, 'list'] as const,
    detail: (id: string) =>
      [...queryKeys.prescriptions.all, 'detail', id] as const,
  },

  // ユーザー設定
  userSettings: {
    all: ['userSettings'] as const,
    current: () => [...queryKeys.userSettings.all, 'current'] as const,
  },

  // 医薬品マスタ
  medicines: {
    all: ['medicines'] as const,
    ippan: () => [...queryKeys.medicines.all, 'ippan'] as const,
    specific: (ippanId: string) =>
      [...queryKeys.medicines.all, 'specific', ippanId] as const,
  },

  // 薬剤変更ログ
  medChangeLogs: {
    all: ['medChangeLogs'] as const,
    list: () => [...queryKeys.medChangeLogs.all, 'list'] as const,
  },

  // 薬局 (Annotator)
  pharmacies: {
    all: ['pharmacies'] as const,
    list: () => [...queryKeys.pharmacies.all, 'list'] as const,
  },

  // Annotator 処方箋
  annotatePrescriptions: {
    all: ['annotatePrescriptions'] as const,
    list: (pharmacyId: string) =>
      [...queryKeys.annotatePrescriptions.all, 'list', pharmacyId] as const,
  },

  // 統計
  statistics: {
    all: ['statistics'] as const,
    requestEvent: () => [...queryKeys.statistics.all, 'requestEvent'] as const,
  },

  // 通知
  notifications: {
    all: ['notifications'] as const,
    clientUpdate: () =>
      [...queryKeys.notifications.all, 'clientUpdate'] as const,
  },

  // ヘルスチェック
  health: {
    all: ['health'] as const,
    check: () => [...queryKeys.health.all, 'check'] as const,
  },

  // 認証
  auth: {
    all: ['auth'] as const,
    check: () => [...queryKeys.auth.all, 'check'] as const,
  },

  // OCR設定
  ocrSettings: {
    all: ['ocrSettings'] as const,
    reshape: () => [...queryKeys.ocrSettings.all, 'reshape'] as const,
    alert: () => [...queryKeys.ocrSettings.all, 'alert'] as const,
  },

  // 用法マスタ登録
  jahisMaster: {
    all: ['jahisMaster'] as const,
    presets: () => [...queryKeys.jahisMaster.all, 'presets'] as const,
  },

  // 本部アカウント
  headquarters: {
    all: ['headquarters'] as const,
    pharmacies: () => [...queryKeys.headquarters.all, 'pharmacies'] as const,
    // 遠隔入力: 指定店舗の未確定処方箋一覧
    storePrescriptions: (pharmacyId: string) =>
      [...queryKeys.headquarters.all, 'prescriptions', pharmacyId] as const,
  },

  // JAHIS-NSIPS突合
  jahisNsipsMatching: {
    all: ['jahisNsipsMatching'] as const,
    stats: (startDate: string, endDate: string, pharmacyId?: string) =>
      [
        ...queryKeys.jahisNsipsMatching.all,
        'stats',
        startDate,
        endDate,
        pharmacyId,
      ] as const,
    mismatches: (
      startDate: string,
      endDate: string,
      field?: string,
      pharmacyId?: string
    ) =>
      [
        ...queryKeys.jahisNsipsMatching.all,
        'mismatches',
        startDate,
        endDate,
        field,
        pharmacyId,
      ] as const,
  },
} as const
